import { onMounted, onUnmounted, ref } from 'vue'

/**
 * useMoveDom
 * 说明：
 * - 用法：在组件 `setup()` 中调用 `const { moveDOM } = useMoveDom()`，
 *   在模板上将目标元素绑定到 `ref`：`<div ref="moveDOM">...</div>`。
 *   布局是 position ：abs
 * - 功能：允许通过鼠标按住元素并拖拽移动位置，移动时会限制元素不超出视口（viewport）
 * - 返回：{ moveDOM }
 */
export function useMoveDom() {
  // 鼠标/触摸在元素上的相对偏移（用于保持鼠标指针与元素内的相对位置）
  let offsetX = 0
  let offsetY = 0
  // 绑定到目标元素的 ref
  const moveDOM = ref(null)
  // 鼠标或触摸按下时的处理函数（开始拖拽）
  function mousedown(e) {
    // 阻止默认行为与冒泡（例如避免触发文本选中或页面滚动）
    e.stopPropagation()
    const el = moveDOM.value
    if (!el) return

    // 计算鼠标相对于元素左上角的偏移量（避免鼠标跳到元素左上角）
    offsetX = e.clientX - el.offsetLeft
    offsetY = e.clientY - el.offsetTop
    // 拖拽移动处理：根据指针位置与偏移计算新的 left/top，并限制在视口内
    function moveHandler(e) {
      e.stopPropagation()
      // 计算目标位置
      let newLeft = e.clientX - offsetX
      let newTop = e.clientY - offsetY

      // 视口尺寸与元素尺寸
      const vw = window.innerWidth
      const vh = window.innerHeight
      const elW = el.offsetWidth
      const elH = el.offsetHeight
      // 边界限制：left/top 保证在 [0, viewport - elementSize] 区间
      newLeft = Math.max(0, Math.min(newLeft, vw - elW))
      newTop = Math.max(0, Math.min(newTop, vh - elH))

      el.style.left = newLeft + 'px'
      el.style.top = newTop + 'px'
    }

    // 抬起时移除移动和抬起监听（避免内存泄漏）
    function upHandler() {
      document.removeEventListener('mousemove', moveHandler)
      document.removeEventListener('mouseup', upHandler)
    }

    // 将监听绑定到 document 上，避免光标离开元素区域导致事件丢失
    document.addEventListener('mousemove', moveHandler)
    document.addEventListener('mouseup', upHandler)
  }

  onMounted(() => {
    const el = moveDOM.value
    if (!el) return
    // 元素本身监听按下事件以启动拖拽
    el.addEventListener('mousedown', mousedown)
  })

  onUnmounted(() => {
    const el = moveDOM.value
    if (el) {
      el.removeEventListener('mousedown', mousedown)
    }
  })

  return { moveDOM }
}
